import { AttributeCard } from "@/components/Card/AttributeCard";
import { Card } from "@/components/Card/card";
import StepFooter from "@/components/Footer/StepFooter";
import Input from "@/components/Input/Input";
import InputTextArea from "@/components/Input/InputTextArea";
import { Spacer, VStack } from "@/components/Layout";
import { ControlledSelect } from "@/components/Select/ControlledSelect";
import { TextVariant } from "@/components/Typography";
import { Text } from "@/components/Typography/Text";
import { DestinationTypeEnum } from "@/constants/network";
import useZodValidation from "@/hooks/useZodValidation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import {
  getDevicesForSelect,
  getPlacesForSelect,
  getWorkersForSelect,
} from "@/routes/_staff/networks/-network-components-api";
import { FormField } from "@/routes/_staff/person/-components/form/FormField";
import { useNavigate } from "@tanstack/react-router";
import { ManualFormHash } from "../-constants/constant";
import { useCreateNetwork } from "../-form/createNetworkApi";
import { useAddNetworkFormStore } from "../-store/add-network-form-store";
import { useNetworkFormHelpers } from "../../-hooks/useNetworkFormHelpers";

export function Step1Form() {
  useZodValidation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks",
  });
  const navigate = useNavigate();
  const store = useAddNetworkFormStore();
  const { submitNetwork, isSubmitting } = useCreateNetwork();

  const { networkTypeOptions, connectionTypeOptions, networkCategoryOptions } =
    useNetworkFormHelpers();

  const destinationTypeOptions = [
    {
      value: DestinationTypeEnum.PLACE,
      label: t("detail.destinationTypes.place"),
    },
    {
      value: DestinationTypeEnum.DEVICE,
      label: t("detail.destinationTypes.device"),
    },
  ];

  const handleNext = async () => {
    if (store.mustOnly) {
      await submitNetwork();
    } else {
      navigate({ hash: ManualFormHash.accessControl });
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <VStack className="gap-6">
        <Card className="p-4">
          <Text variant="h3">{`1. ${t("add.step1.title")}`}</Text>
        </Card>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.networkName.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <Input
                id="networkName"
                value={store.networkName}
                onChange={(e) => store.update("networkName", e.target.value)}
                error={!!store.errors.networkName}
              />
            }
            errorMessage={store.errors.networkName}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.networkCategory.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="networkCategory"
                value={store.networkCategory}
                onChange={(option) =>
                  store.update(
                    "networkCategory",
                    option?.value as typeof store.networkCategory,
                  )
                }
                fetchOptions={() => Promise.resolve(networkCategoryOptions)}
                hideSearch={true}
                error={!!store.errors.networkCategory}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.networkCategory}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.networkType.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="networkType"
                value={store.networkType}
                onChange={(option) =>
                  store.update(
                    "networkType",
                    option?.value as typeof store.networkType,
                  )
                }
                fetchOptions={() => Promise.resolve(networkTypeOptions || [])}
                hideSearch={true}
                error={!!store.errors.networkType}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.networkType}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.destinationType.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="destinationType"
                value={store.destinationType}
                onChange={(option) => {
                  const newDestinationType =
                    option?.value as typeof store.destinationType;

                  // destinationTypeを更新
                  store.update("destinationType", newDestinationType);

                  // 非活性になる方の値をリセット
                  if (newDestinationType === DestinationTypeEnum.DEVICE) {
                    store.update("placeId", undefined);
                  } else if (newDestinationType === DestinationTypeEnum.PLACE) {
                    store.update("deviceId", undefined);
                  } else {
                    // 未選択の場合は両方リセット
                    store.update("placeId", undefined);
                    store.update("deviceId", undefined);
                  }
                }}
                fetchOptions={() =>
                  Promise.resolve(destinationTypeOptions || [])
                }
                hideSearch={true}
                error={!!store.errors.destinationType}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.destinationType}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.placeId.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="placeId"
                value={store.placeId}
                disabled={store.destinationType !== DestinationTypeEnum.PLACE}
                onChange={(option) =>
                  store.update("placeId", option?.value || undefined)
                }
                fetchOptions={(text?: string) => getPlacesForSelect(text)}
                error={!!store.errors.placeId}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.placeId}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.deviceId.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="deviceId"
                value={store.deviceId}
                disabled={store.destinationType !== DestinationTypeEnum.DEVICE}
                onChange={(option) =>
                  store.update("deviceId", option?.value || undefined)
                }
                fetchOptions={(text?: string) => getDevicesForSelect(text)}
                error={!!store.errors.deviceId}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.deviceId}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.connectionType.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="connectionType"
                value={store.connectionType}
                onChange={(option) =>
                  store.update(
                    "connectionType",
                    option?.value as typeof store.connectionType,
                  )
                }
                fetchOptions={() =>
                  Promise.resolve(connectionTypeOptions || [])
                }
                hideSearch={true}
                error={!!store.errors.connectionType}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.connectionType}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("add.step1.purpose.label")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <InputTextArea
                id="purpose"
                value={store.purpose}
                onChange={(e) => store.update("purpose", e.target.value)}
                error={!!store.errors.purpose}
                rows={3}
              />
            }
            errorMessage={store.errors.purpose}
            optional={true}
          />
        </AttributeCard>

        <AttributeCard>
          <FormField
            className="mt-4"
            fieldTitle={t("detail.fields.adminContact")}
            titleVariant={TextVariant.CardHeader}
            titleClassName="text-neutral-on-fill"
            fieldInput={
              <ControlledSelect
                id="adminContact"
                value={store.adminContact}
                onChange={(option) =>
                  store.update(
                    "adminContact",
                    (option?.value as string) ?? undefined,
                  )
                }
                fetchOptions={(text?: string) => getWorkersForSelect(text)}
                error={!!store.errors.adminContact}
                allowEmpty={true}
              />
            }
            errorMessage={store.errors.adminContact}
            optional={true}
          />
        </AttributeCard>
      </VStack>
      <Spacer variant="xxl" />
      <StepFooter
        rightButton={{
          onClick: handleNext,
          label: isSubmitting
            ? t("add.step1.submitting")
            : `2. ${t("add.step1.next")}`,
          disabled: isSubmitting,
        }}
      />
    </form>
  );
}
